import _ from 'lodash';
import { Person } from './BaseModels';
import dateUtil from '../utils/dates';
import WhiteLabelController from '../core/WhiteLabelController';
import { capitalizeName } from '../components/filters/utils';

const parseBooleanFlag = (value) => value === true || value === 'true' || value === 1 || value === '1';

export class User extends Person {

	static AD_SPENDS = {
		LOW: '$0-$100K',
		MEDIUM: '$100K-$250K',
		HIGH: '$250k+'
	};

	static USER_TYPES = {
		BRAND: 'brand',
		AGENCY: 'agency'
	};

	static STATUSES = {
		PENDING: 'pending',
		REJECTED: 'rejected',
		ACTIVE: 'active',
		EXPIRED: 'expired'
	};

	static fromJSON(json) {
		const fullname = _.trimStart(`${json.first_name || ''} ${json.last_name || ''}`);
		return _.extend(super.fromJSON(json), {
			id: json.id,
			perms: json.perms,
			authToken: json.token,
			company: json.company,
			phoneNumber: json.phone_number,
			phoneNumberVerified: json.phone_number_verified,
			profileImageUrl: json.profile_image_url,
			logoUrl: json.logo_url,
			hasAcceptedGDPR: json.has_accepted_GDPR,
			registrationDate: dateUtil.dateToLocalZone(json.date_joined),
			lastLoginDate: dateUtil.dateToLocalZone(json.last_login),
			historicalAWAccountId: json.historical_aw_account,
			hasDisapprovedAd: json.has_disapproved_ad,
			annualAdSpend: json.annual_ad_spend,
			userType: json.user_type,
			isImpersonating: json.isImpersonating,
			// only for few users
			hideBrandName: json.hide_brand_name,
			hideChannelsAndVideosOnDashboard: json.hide_channels_and_videos_on_dashboard,
			status: json.status,
			domain: json.domain,
			region: json.region_id,
			country: json.country_id,
			countryName: json.country_name,
			countryAlphaCode: json.country_alpha_code,
			domainId: json.domain_id || json.domain?.id,
			role: json.role,
			roleId: json.role_id,
			isActive: parseBooleanFlag(json.is_active),
			firstName: json.first_name,
			lastName: json.last_name,
			userFullName: !_.isEmpty(fullname) ? capitalizeName(fullname) : 'N/A',
			userSiteLogo: json.logo_url,
			persona: json.persona,
			userGroup: json.user_group
		});
	}

	static toJSON(data) {
		return {
			first_name: data.firstName,
			last_name: data.lastName,
			company: data.company,
			phone_number: data.phoneNumber,
			email: data.email,
			has_accepted_GDPR: data.has_accepted_GDPR,
			verify_password: data.verifyPassword,
			password: data.password,
			annual_ad_spend: data.annualAdSpend,
			user_type: data.userType,
			region_id: data.region,
			country_id: data.country
		};
	}

	get isAdminUser() {
		return this.checkAccess('admin');
	}

	get canAccessComscoreEditor() {
		return this.checkAccess('comscore_editor') || this.isAdminUser;
	}

	get canAccessUmbrellaTopics() {
		return this.checkAccess('nlp_umbrella') || this.checkAccess('admin');
	}

	get canAccessAdmin() {
		return this.canAccessUserManager || this.canAccessUserAnalytics || this.canAccessDomainManager || this.canAccessBSTE || this.canAccessCIDManager ||
			this.canAccessBlocklist || this.canAccessDataTracker || this.canAccessComscoreEditor || this.canAccessUmbrellaTopics || this.canAccessCPRScript;
	}

	get canAccessCIDManager() {
		return this.checkAccess('cid_manager') || this.isAdminUser;
	}

	get canAccessCPRScript() {
		return this.checkAccess('admin.cpr_script') || this.isAdminUser;
	}

	get canAccessAdsAnalyzerRecipients() {
		return this.checkAccess('ads_analyzer.recipients') || this.isAdminUser;
	}

	get canAccessBrandProfiles() {
		return this.checkAccess('suitability_profiles') || this.isAdminUser;
	}
	get canAccessSuitabilityProfileCreateEdit() {
		return this.checkAccess('suitability_profiles.create_edit') || this.isAdminUser;
	}
	get canAccessSuitabilityProfileCreateEditAll() {
		return this.checkAccess('suitability_profiles.create_edit_all') || this.isAdminUser;
	}

	get canAccessAuditQueue() {
		return (this.checkAccess('audit_queue') && this.domainAccessToAuditQueue) || this.isAdminUser;
	}

	get canAccessAuditQueueCreate() {
		return this.checkAccess('audit_queue.create') || this.isAdminUser;
	}

	get canAccessAuditQueuePriority() {
		return this.checkAccess('audit_queue.set_priority') || this.isAdminUser;
	}

	get canAccessAuditQueueImages() {
		return this.checkAccess('audit_queue.images') || this.isAdminUser;
	}

	get canAccessAuditQueueUrgent() {
		return this.checkAccess('audit_queue.urgent_mode') || this.isAdminUser;
	}

	get canAccessCustomTagger() {
		return this.checkAccess('channel_tagger') || this.isAdminUser;
	}

	get canAccessCustomTagger() {
		return this.checkAccess('channel_tagger') || this.isAdminUser;
	}

	get canAccessCustomTaggerMeta() {
		return this.checkAccess('channel_tagger.meta_manual_tagging') || this.isAdminUser;
	}

	get canAccessBlocklist() {
		return this.checkAccess('blocklist_manager') || this.isAdminUser;
	}

	get canAccessBlocklistCreateChannel() {
		return this.checkAccess('blocklist_manager.create_channel') || this.isAdminUser;
	}

	get canAccessBlocklistDeleteChannel() {
		return this.checkAccess('blocklist_manager.delete_channel') || this.isAdminUser;
	}

	get canAccessBlocklistExportChannel() {
		return this.checkAccess('blocklist_manager.export_channel') || this.isAdminUser;
	}

	get canAccessBlocklistCreateVideo() {
		return this.checkAccess('blocklist_manager.create_video') || this.isAdminUser;
	}

	get canAccessBlocklistDeleteVideo() {
		return this.checkAccess('blocklist_manager.delete_video') || this.isAdminUser;
	}

	get canAccessBlocklistExportVideo() {
		return this.checkAccess('blocklist_manager.export_video') || this.isAdminUser;
	}

	get canAccessBlocklistEditVideo() {
		return this.checkAccess('blocklist_manager.edit_video') || this.isAdminUser;
	}

	get canAccessBlocklistEditChannel() {
		return this.checkAccess('blocklist_manager.edit_channel') || this.isAdminUser;
	}

	get canAccessBSTE() {
		return this.checkAccess('bste') || this.isAdminUser;
	}

	get canAccessBSTECreate() {
		return this.checkAccess('bste.create') || this.isAdminUser;
	}

	get canAccessBSTEDelete() {
		return this.checkAccess('bste.delete') || this.isAdminUser;
	}

	get canAccessBSTEExport() {
		return this.checkAccess('bste.export') || this.isAdminUser;
	}

	/** Sidebar: "Legacy Build" tab (`/legacy_build`). Permission: `build.legacy_build` */
	get canAccessBuild() {
		return this.checkAccess('build.legacy_build') || this.isAdminUser;
	}

	get canAccessURLConverter() {
		return this.checkAccess('url_converter') || this.isAdminUser;
	}

	/** Sidebar: "Build" tab (`/build`) — New Agentic Build. Permission: `agentic_build` */
	get canAccessAgenticBuild() {
		return this.checkAccess('agentic_build') || this.isAdminUser;
	}

	/** Sidebar: "Advanced Build" tab (`/advanced_build`). Permission: `build` */
	get canAccessAlternateBuild() {
		return this.checkAccess('build') || this.isAdminUser;
	}

	get canAccessBuildExpirationWarning() {
		return this.checkAccess('build.expiration_warning') || this.isAdminUser;
	}

	get canAccessBuildSeeCardFilters() {
		return this.checkAccess('build.ctl_show_used_filters') || this.isAdminUser;
	}

	get canAccessBuildDV360Extension() {
		return this.checkAccess('build.dv360_extension') || this.isAdminUser;
	}

	get canAccessBuildGAdsExtension() {
		return this.checkAccess('build.gads_extension') || this.isAdminUser;
	}

	get canAccessBuildDomainsShare() {
		return this.checkAccess('build.ctl_share_with_domains') || this.isAdminUser;
	}

    get canAccessBuildDashboard() {
        return this.checkAccess('build.ctl_inventory') || this.isAdminUser;
    }

	get canAccessBuildRolesShare() {
		return this.checkAccess('build.ctl_share_with_roles') || this.isAdminUser;
	}

	get canAccessBuildDV360Reach() {
		return this.checkAccess('build.dv360_reach') || this.isAdminUser;
	}

	get canAccessBuildUsersShare() {
		return this.checkAccess('build.ctl_share_with_users') || this.isAdminUser;
	}

	get canAccessBuildPushGAds() {
		return this.checkAccess('build.ctl_push_to_gads') || this.isAdminUser;
	}

	get canAccessCTLCreate() {
		return this.checkAccess('build.ctl_create') || this.isAdminUser;
	}

	get canAccessCTLCreateVideo() {
		return this.checkAccess('build.ctl_create_video_list') || this.isAdminUser;
	}

	get canAccessBuildOracle() {
		return this.checkAccess('build.ctl_oracle') || this.isAdminUser;
	}

	get canAccessCTLDelete() {
		return this.checkAccess('build.ctl_delete') || this.isAdminUser;
	}

	get canAccessCTLDeleteVideo() {
		return this.checkAccess('build.ctl_delete_video_list') || this.isAdminUser;
	}

	get canAccessBuildEditAll() {
		return this.checkAccess('build.ctl_edit_all') || this.isAdminUser;
	}

	get canAccessCTLExport() {
		return this.checkAccess('build.ctl_export_basic') || this.isAdminUser;
	}

	get canAccessCTLExportAdmin() {
		return this.checkAccess('build.ctl_export_admin') || this.isAdminUser;
	}

	get canAccessCTLConscious() {
		return this.checkAccess('build.ctl_pillar_associations') || this.isAdminUser;
	}

	get canAccessCTLFeature() {
		return this.checkAccess('build.ctl_feature_list') || this.isAdminUser;
	}

	get canAccessCTLSeeAll() {
		return this.checkAccess('build.ctl_see_all') || this.isAdminUser;
	}

	get canAccessCTLVetEnable() {
		return this.checkAccess('build.ctl_vet_enable') || this.isAdminUser;
	}

	get canAccessCTLVet() {
		return this.checkAccess('build.ctl_vet') || this.isAdminUser;
	}

	get canAccessCTLVetBasic() {
		return this.checkAccess('build.ctl_vet_basic') || this.isAdminUser;
	}

	get canAccessCTLCELists() {
		return this.checkAccess('build.ctl_assign_standard_chrome_extension_lists') || this.isAdminUser;
	}

	get canAccessCustomVettingData() {
		return this.checkAccess('build.ctl_custom_vetting_data') || this.isAdminUser;
	}

	get canAccessComscore() {
		return this.checkAccess('build.ctl_comscore_segments') || this.isAdminUser;
	}

    get canExportAllListsComscore() {
        return this.checkAccess('build.ctl_export_all_lists_comscore') || this.isAdminUser;
    }

	get canAccessReportVettingIssue() {
		return this.checkAccess('build.ctl_report_vetting_issue') || this.isAdminUser;
	}

	get canAccessCTLCSVUpload() {
		return this.checkAccess('build.ctl_from_custom_list') || this.isAdminUser;
	}

	get canAccessCTLVetExport() {
		return this.checkAccess('build.ctl_vet_export') || this.isAdminUser;
	}

	get canAccessCTLVideoEx() {
		return this.checkAccess('build.ctl_video_exclusion') || this.isAdminUser;
	}

	get canAccessCTLSentiment() {
		return this.checkAccess('build.ctl_sentiment') || this.isAdminUser;
	}

	get canAccessTemplates() {
		return this.checkAccess('build.ctl_params_template') || this.isAdminUser;
	}

	get canAccessPrimaryCategories() {
		return this.checkAccess('build.ctl_relevant_primary_categories') || this.isAdminUser;
	}

	get canAccessIABTwoCategories() {
		return this.checkAccess('build.ctl_iab_categories_2') || this.isAdminUser;
	}

	get canAccessGarmSuitability() {
		return this.checkAccess('build.ctl_advanced_suitability') || this.isAdminUser;
	}
	get canAccessNLPSuitability() {
		return this.checkAccess('build.nlp_suitability') || this.isAdminUser;
	}

	get canAccessMetaSuitability() {
		return this.checkAccess('build.meta_suitability') || this.isAdminUser;
	}

	get canAccessNLPTopics() {
		return this.checkAccess('build.ctl_nlp_topics') || this.isAdminUser;
	}

	get canAccessNLPCuratedUmbrellas() {
		return this.checkAccess('build.nlp_umbrellas') || this.isAdminUser;
	}

	get canAccessProgrammaticAge() {
		return this.checkAccess('build.ctl_programmatic_age') || this.isAdminUser;
	}

	get canAccessProgrammaticGender() {
		return this.checkAccess('build.ctl_programmatic_gender') || this.isAdminUser;
	}

	get canAccessBuildChatbot() {
		return this.checkAccess('build.chatbot') || this.isAdminUser;
	}

	get canAccessBuildExclusions() {
		return this.checkAccess('build.ctl_exclusion_list_business') || this.isAdminUser;
	}

	get canAccessBuildExclusionsYTExports() {
		return this.checkAccess('build.ctl_exclusion_yt_export') || this.isAdminUser;
	}

	get canAccessExportAll() {
		return this.checkAccess('export_admin') || this.isAdminUser;
	}

	get canAccessDomainManager() {
		return this.checkAccess('domain_manager') || this.isAdminUser;
	}

	get canDeleteDomains() {
		return this.checkAccess('domain_manager.delete') || this.isAdminUser;
	}

	get canAccessDomainManagerCreate() {
		return this.checkAccess('domain_manager.create') || this.isAdminUser;
	}

	get canAccessDomainManagerDelete() {
		return this.checkAccess('domain_manager.delete') || this.isAdminUser;
	}

	get canAccessManagedServiceExport() {
		return this.checkAccess('managed_service.export') || this.isAdminUser;
	}

	get canAccessManagedServicePerformanceGraph() {
		return this.checkAccess('managed_service.performance_graph') || this.isAdminUser;
	}

	get canAccessManagedServiceDelivery() {
		return this.checkAccess('managed_service.delivery') || this.isAdminUser;
	}

	get canAccessManagedServiceCampaignsSegmented() {
		return this.checkAccess('managed_service.campaigns_segmented') || this.isAdminUser;
	}

	get canAccessManagedServiceConversions() {
		return this.checkAccess('managed_service.conversions') || this.isAdminUser;
	}

	get canAccessManagedServiceAllAccounts() {
		return this.checkAccess('managed_service.visible_all_accounts') || this.isAdminUser;
	}

	get canAccessManagedServiceRealGAdsCost() {
		return this.checkAccess('managed_service.real_gads_cost') || this.isAdminUser;
	}

	get canAccessManagedServiceGlobalAccount() {
		return this.checkAccess('managed_service.global_account_visibility') || this.isAdminUser;
	}

	get canAccessManagedServiceAudiences() {
		return this.checkAccess('managed_service.audiences') || this.isAdminUser;
	}

	get canAccessManagedServiceCost() {
		return this.checkAccess('managed_service.service_costs') || this.isAdminUser;
	}

	get canAccessManagedServiceChannelVideoTabs() {
		return this.checkAccess('managed_service.channel_video_tabs') || this.isAdminUser;
	}

	get canAccessMyCustomData() {
		return this.checkAccess('client_custom_data') || this.isAdminUser;
	}

	get canAccessMyCustomDataAll() {
		return this.checkAccess('client_custom_data.see_all') || this.isAdminUser;
	}

	get canAccessMyCustomDataDomainsShare() {
		return this.checkAccess('client_custom_data.share_with_domains') || this.isAdminUser;
	}

	get canAccessMyCustomDataRolesShare() {
		return this.checkAccess('client_custom_data.share_with_roles') || this.isAdminUser;
	}

	get canAccessMyCustomDataUsersShare() {
		return this.checkAccess('client_custom_data.share_with_users') || this.isAdminUser;
	}

	get canAccessSeeNotifications() {
		return this.checkAccess('in_app_notifications') || this.isAdminUser;
	}

	get canAccessNotificationsManagerCreate() {
		return this.checkAccess('in_app_notifications.manage_in_app_notifications') || this.isAdminUser;
	}

	get canAccessPacingReport() {
		return (this.checkAccess('pacing_report') && this.domainAccessToPacingReport) || this.isAdminUser;
	}

	get canAccessInspectYouTube() {
		return this.checkAccess('inspect_youtube') || this.isAdminUser;
	}

	get canAccessInspectYouTubeVettedStatus() {
		return this.checkAccess('inspect_youtube.vetted_status') || this.isAdminUser;
	}

	get canAccessInspectMeta() {
		return this.checkAccess('inspect_meta') || this.isAdminUser;
	}

	get canAccessInspectYouTubeNLPAgeClassification() {
		return this.checkAccess('inspect_youtube.nlp_age_classification') || this.isAdminUser;
	}

	get canAccessInspectMetaNLPAgeClassification() {
		return this.checkAccess('inspect_meta.nlp_age_classification') || this.isAdminUser;
	}

	get canAccessInspectMetaBusinessManager() {
		return this.checkAccess('inspect_meta.meta_business_manager') || this.isAdminUser;
	}

	get canAccessInspectYouTubeShare() {
		return this.checkAccess('inspect_youtube.inspect_share') || this.isAdminUser;
	}

	get canAccessInspectMetaShare() {
		return this.checkAccess('inspect_meta.inspect_share') || this.isAdminUser;
	}

	get canAccessInspectYouTubeManageAll() {
		return this.checkAccess('inspect_youtube.manage_all') || this.isAdminUser;
	}

	get canAccessInspectMetaManageAll() {
		return this.checkAccess('inspect_meta.manage_all') || this.isAdminUser;
	}

	get canAccessInspectYouTubeOptimizations() {
		return this.checkAccess('inspect_youtube.access_to_optimize_exclusions') || this.isAdminUser;
	}

	get canAccessInspectYouTubeExportAll() {
		return this.checkAccess('inspect_youtube.export_all') || this.isAdminUser;
	}

	get canAccessInspectMetaExportAll() {
		return this.checkAccess('inspect_meta.export_all') || this.isAdminUser;
	}

	get canAccessInspectYouTubeSuitability() {
		return this.checkAccess('inspect_youtube.brand_suitability') || this.isAdminUser;
	}

	get canAccessInspectMetaSuitability() {
		return this.checkAccess('inspect_meta.brand_suitability') || this.isAdminUser;
	}

	get canAccessInspectYouTubeFlagging() {
		return this.checkAccess('inspect_youtube.flag_inspection') || this.isAdminUser;
	}

	get canAccessInspectYouTubeRecommendations() {
		return this.checkAccess('inspect_youtube.recommendations_tab') || this.isAdminUser;
	}

	get canAccessInspectYouTubeAnalyticsDashboard() {
		return this.checkAccess('inspect_youtube.access_inspect_analytics_dashboard') || this.isAdminUser;
	}

	get canAccessTrends() {
		return this.checkAccess('trends') || this.isAdminUser;
	}

	get canAccessTargetingAgent() {
		return this.checkAccess('elev8') || this.isAdminUser;
	}

	get canArchivePersonas() {
		return this.checkAccess('elev8.archive_personas') || this.isAdminUser;
	}

	get canArchiveCampaigns() {
		return this.checkAccess('elev8.archive_campaigns') || this.isAdminUser;
	}

	get canAccessExtLabs() {
		return this.checkAccess('iq_external_labs') || this.isAdminUser;
	}

	get canAccessExtLabsTrendiq() {
		return this.checkAccess('iq_external_labs_trendiq') || this.isAdminUser;
	}

	get canAccessExtAnalytics() {
		return this.checkAccess('iq_external_analytics') || this.isAdminUser;
	}

	get canAccessExtAnalyticsAiChatbot() {
		return this.checkAccess('iq_external_analytics.ai_chatbot') || this.isAdminUser;
	}

	get canAccessMetaInspectBrightData() {
		return this.checkAccess('inspect_meta.meta_campaigns_bright_data') || this.isAdminUser;
	}

	get canAccessMetaInspectMetaCampaigns() {
		return this.checkAccess('inspect_meta.meta_campaigns_content') || this.isAdminUser;
	}

	get canAccessMetaInspectDlhDemo() {
		return this.checkAccess('inspect_meta.meta_campaigns_dlh_demo') || this.isAdminUser;
	}

	get canAccessForecasting() {
		return (this.checkAccess('pricing_tool') && this.domainAccessToForecasting) || this.isAdminUser;
	}

	get canAccessResearch() {
		return (this.checkAccess('research') && this.domainAccessToResearch) || this.isAdminUser;
	}

	get canAccessResearchAuth() {
		return this.checkAccess('research.auth') || this.isAdminUser;
	}

	get canAccessExploreCtv() {
		return this.checkAccess('research.ctv') || this.isAdminUser;
	}

	get canAccessExploreReddit() {
		return this.checkAccess('research.reddit') || this.isAdminUser;
	}

	get canAccessHistoricalAdsPerformance() {
		return this.checkAccess('research.historical_ads_performance') || this.isAdminUser;
	}

	get canAccessResearchAgeGender() {
		return this.checkAccess('research.age_gender') || this.isAdminUser;
	}

	get canAccessResearchBrandSuitabilityOverride() {
		return this.checkAccess('research.brand_suitability_v2_override') || this.isAdminUser;
	}

	get canAccessResearchBrandSuitability() {
		return this.checkAccess('research.brand_suitability') || this.isAdminUser;
	}

	get canAccessResearchBrandSuitabilityHighRisk() {
		return this.checkAccess('research.brand_suitability_high_risk') || this.isAdminUser;
	}

	get canAccessResearchDetail() {
		return (this.checkAccess('research.channel_video_view') && this.domainAccessToResearch) || this.isAdminUser;
	}

	get canAccessResearchLanguageData() {
		return this.checkAccess('research.language_data') || this.isAdminUser;
	}

	get canAccessResearchLanguageFlagging() {
		return this.checkAccess('research.language_flagging') || this.isAdminUser;
	}

	get canAccessResearchMonetization() {
		return this.checkAccess('research.monetization') || this.isAdminUser;
	}

	get canAccessResearchExport() {
		return this.checkAccess('research.export') || this.isAdminUser;
	}

	get canAccessExploreSummaryCards() {
		return this.checkAccess('research.summary_cards') || this.isAdminUser;
	}

	get canAccessResearchTranscripts() {
		return this.checkAccess('research.transcripts') || this.isAdminUser;
	}

	get canAccessResearchVetting() {
		return this.checkAccess('research.vetting') || this.isAdminUser;
	}

	get canAccessResearchVettingBasic() {
		return this.checkAccess('research.vetting_basic') || this.isAdminUser;
	}

	get canAccessResearchVettingData() {
		return this.checkAccess('research.vetting_data') || this.isAdminUser;
	}

	get canAccessResearchDislikes() {
		return this.checkAccess('research.sentiment') || this.isAdminUser;
	}

	get canAccessResearchAITopics() {
		return this.checkAccess('research.ai_topics') || this.isAdminUser;
	}

	get canAccessUserAnalytics() {
		return this.checkAccess('user_analytics') || this.isAdminUser;
	}

	get canAccessUserManager() {
		if (this.isImpersonating) return false;
		return this.checkAccess('user_management') || this.isAdminUser;
	}

	get canAccessPlatformSwitcher() {
		return this.checkAccess('platform_switcher') || this.isAdminUser;
	}

	get canAccessPlatformSwitcherViewIq() {
		return this.checkAccess('platform_switcher.view_iq') || this.isAdminUser;
	}

	get canAccessPlatformSwitcherActivateIq() {
		return this.checkAccess('platform_switcher.activate_iq') || this.isAdminUser;
	}

	get canAccessPlatformSwitcherReportingIq() {
		return this.checkAccess('platform_switcher.reporting_iq') || this.isAdminUser;
	}

	get canAccessPlatformSwitcherAero() {
		return this.checkAccess('platform_switcher.aero') || this.isAdminUser;
	}

	get canAccessUserManagerCreateRoles() {
		return this.checkAccess('user_management.create_user_roles') || this.isAdminUser;
	}

	get canAccessUserManagerAssignRoles() {
		return this.checkAccess('user_management.assign_user_roles') || this.isAdminUser;
	}

	get canAccessUserManagerEditPermissions() {
		return this.checkAccess('user_management.edit_custom_permissions') || this.isAdminUser;
	}

	get canAccessUserManagerEditUsersRoles() {
		return this.checkAccess('user_management.edit_user_roles') || this.isAdminUser;
	}

	get canAccessUserManagerDeleteRoles() {
		return this.checkAccess('user_management.delete_user_roles') || this.isAdminUser;
	}

	get canAccessUserManagerEditUserSettings() {
		return this.checkAccess('user_management.edit_user_settings') || this.isAdminUser;
	}

	get canAccessUserManagerExport() {
		return this.checkAccess('user_management.export_user_list') || this.isAdminUser;
	}

	get canAccessUserManagerLogInAsUser() {
		return this.checkAccess('user_management.log_in_as_user') || this.isAdminUser;
	}

	get canAccessTools() {
		if (this.canAccessNewNavigation) return this.canAccessAuditQueue || this.canAccessMyCustomData || this.canAccessCustomTagger || this.canAccessBrandProfiles || this.canAccessAdBidder || this.isAdminUser;
		return this.canAccessPacingReport || this.canAccessAuditQueue || this.canAccessMyCustomData || this.canAccessCustomTagger || this.canAccessPacingIQ || this.canAccessBrandProfiles || this.canAccessAdBidder || this.isAdminUser;
	}

	get canAccessNewNavigation() {
		return this.checkAccess('side_navigation');
	}

	get canAccessResearchIASOnly() {
		return this.checkAccess('research.ias_only') && !this.isAdminUser;
	}

	get canAccessBuildIASOnly() {
		return this.checkAccess('build.ctl_ias_only') && !this.isAdminUser;
	}

	get canAccessDataTracker() {
		return this.checkAccess('data_tracker') || this.isAdminUser;
	}

	get canAccessServiceDesk() {
		return this.checkAccess('service_desk') || this.isAdminUser;
	}

	// TODO: Temporary until Explore V2 launches as Explore
	get canAccessExploreV2() {
		return this.checkAccess('experimental_features.explore') || this.isAdminUser;
	}

	get canAccessExploreMeta() {
		return this.checkAccess('explore.meta') || this.isAdminUser;
	}

	get canAccessExploreDashboardExport() {
		return this.checkAccess('ai_dashboard_export') || this.isAdminUser;
	}
	
	get canAccessExploreRFP() {
		return this.isAdminUser;
	}

	get canAccessWidgetLocker() {
		return this.checkAccess('ai_widget_locker') || this.isAdminUser;
	}

	get canAccessWidgetLockerDefault() {
		return this.checkAccess('ai_widget_locker.edit_default_view') || this.isAdminUser;
	}

	get canAccessWidgetLockerShare() {
		return this.checkAccess('ai_widget_locker.share_widgets') || this.isAdminUser;
	}

	get canAccessExperimentalFeatures() {
		return this.checkAccess('experimental_features');
	}

	get canAccessStreamliner() {
		return this.checkAccess('streamliner') || this.isAdminUser;
	}

	get canAccessStreamlinerPushGAds() {
		return this.checkAccess('streamliner.push_gads') || this.isAdminUser;
	}

	get canAccessFakeDataClickableDemo() {
		return this.checkAccess('experimental_features.clickable_demo_feature_preview');
	}

	get canAccessAdBidder() {
		return this.checkAccess('auto_bid') || this.isAdminUser;
	}

	get canAccessAdBidderV2() {
		return this.checkAccess('experimental_features.auto_bid_v2') || this.isAdminUser;
	}

	get canAccessPersonas() {
		return this.checkAccess('personas') || this.isAdminUser;
	}

	get canAccessPacingIQ() {
		return this.checkAccess('pacing_iq') || this.isAdminUser;
	}

	get canAccessCrisisNews() {
		return this.checkAccess('crisis') || this.isAdminUser;
	}

	get domainAccessToGoogleOAuth() {
		return this.checkDomainAccess('Google OAuth');
	}

	get domainAccessToResearch() {
		return this.checkDomainAccess('Insights') || this.isAdminUser;
	}

	get domainAccessToBuild() {
		return this.checkDomainAccess('Build') || this.isAdminUser;
	}

    get domainAccessToExportsManager() {
        return this.checkDomainAccess('Exports') || this.isAdminUser;
    }

	get domainAccessToReporting() {
		return this.checkDomainAccess('Reporting+') || this.isAdminUser;
	}

	get domainAccessToSelfService() {
		return this.checkDomainAccess('Analytics > Self Service') || this.isAdminUser;
	}

	get domainAccessToSelfServiceTrends() {
		return this.checkDomainAccess('Analytics > Self Service Trends') || this.isAdminUser;
	}

	get domainAccessToPacingReport() {
		return this.checkDomainAccess('Tools > Pacing Report') || this.isAdminUser;
	}

	get domainAccessToForecasting() {
		return this.checkDomainAccess('Forecasting') || this.isAdminUser;
	}

	get domainAccessToAuditQueue() {
		return this.checkDomainAccess('Tools > Audit Queue') || this.isAdminUser;
	}

	get domainAccessToReview() {
		return this.checkDomainAccess('Review') || this.isAdminUser;
	}

	checkDomainAccess(accessDisabled) {
		const hasDomainAccess = !WhiteLabelController.siteAssets.restricted.includes(accessDisabled);
		return hasDomainAccess;
	}

	checkAccess(accessName) {
		if (!_.isEmpty(this?.perms)) {
			return !!this.perms[accessName];
		}
		return false;
	}

	get isYoutubeDemoUser() {
		return this.email === 'ytmpdemo@gmail.com';
	}
}

export class UserAction {
	static fromJSON(json) {
		return _.extend(new this(), {
			id: json.id,
			slug: json.slug,
			firstName: json.first_name,
			lastName: json.last_name,
			email: json.email,
			url: json.url,
			createdAt: dateUtil.dateToLocalZone(json.created_at)
		});
	}
}

export class LocationPolygon {
	static isPointInPolygon(polygon, lat, lng) {
		let i,
			j,
			c = 0;
		for (i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
			if (polygon[i][0] > lng != polygon[j][0] > lng && lat < ((polygon[j][1] - polygon[i][1]) * (lng - polygon[i][0])) / (polygon[j][0] - polygon[i][0]) + polygon[i][1]) c = !c;
		}
		return c;
	}

	static fromJSON(lat, lng, locations) {
		let potentialCorrectLocations = _.filter(locations, location => _.get(location, 'geojson.type') === 'Polygon' || _.get(location, 'geojson.type') === 'MultiPolygon');

		let correctLocation = _.find(potentialCorrectLocations, location => {
			const sourceCoordinates = _.get(location, 'geojson.coordinates');
			if (_.get(location, 'geojson.type') === 'Polygon') {
				return LocationPolygon.isPointInPolygon(sourceCoordinates[0], lat, lng);
			}
			const coordinates = _.maxBy(sourceCoordinates, o => o[0].length)[0];
			return LocationPolygon.isPointInPolygon(coordinates, lat, lng);
		});

		let polygon = _.get(correctLocation, 'geojson.coordinates');
		if (_.get(correctLocation, 'geojson.type') === 'MultiPolygon') {
			const coordinates = _.get(correctLocation, 'geojson.coordinates');
			polygon = _.maxBy(coordinates, o => o[0].length);
		}
		polygon = _.map(polygon, p =>
			_.map(p, coords => {
				return { lng: coords[0], lat: coords[1] };
			})
		)[0];

		return _.extend(new this(), { polygon: _.size(polygon) > 100 ? polygon : null });
	}
}

export class WikipediaArticleModel {
	static fromJSON(data) {
		const pageId = _.keys(data.query.pages)[0];
		return _.extend(new this(), {
			url: _.get(data.query.pages[pageId], 'canonicalurl'),
			title: _.get(data.query.pages[pageId], 'displaytitle'),
			extract: _.get(data.query.pages[pageId], 'extract'),
			thumbnail: _.get(data.query.pages[pageId], 'thumbnail'),
			originalImage: _.get(data.query.pages[pageId], 'original')
		});
	}
}

export class TagsEditorBadWordModel {
	static fromJSON(json) {
		return _.extend(new this(), {
			id: json.id,
			name: json.name,
			category: json.category,
			language: json.language,
			score: json.negative_score,
			metaScoring: json.meta_scoring,
			exclCategories: json.excl_content_categories,
			killWord: json.kill_word,
			substringMatch: json.substring_match
		});
	}
}

export class TagsEditorHistoryItem {
	static fromJSON(json) {
		return _.extend(new this(), {
			id: json.id,
			tagName: json.tag_name,
			action: json.action,
			createdAt: json.created_at ? new Date(json.created_at) : null
		});
	}
}

export class PermissionsLog {
	static fromJSON(json) {
		return _.extend(new this(), {
			date: json.date,
			positiveAction: _.includes(json.action, 'added'),
			actionUser: json.acting_user,
			value: json.field_value || null,
			targetUser: json.target_user,
			description: json.summary,
			type: json.field_type
		})
	}
}

export class RoleLog {
	static fromJSON(json) {
		return _.extend(new this(), {
			date: dateUtil.formatToOnlyDateFormat(json.date),
			positiveAction: _.includes(json.action, 'added'),
			actionUser: json.acting_user,
			permission: json.permission || null,
			user: json.user || null,
			roleID: json.role_id,
			roleName: json.role_name,
			description: json.summary
		})
	}
}

export class CIDManagerItem {
	static fromJSON(json) {
		return _.extend(new this(), {
			userID: json.user_id,
			userName: json.user_name,
			cid: json.cid,
			createdAt: json.created_at
		})
	}
}

export class BlocklistOptions {
	static fromJSON(json) {
		return _.extend(new this(), {
			blockReasons: json.block_reasons,
			blockRegions: json.block_regions
		})
	}
}

export class BlocklistItem {
	static toJSON(data) {
		return _.extend(new this(), {
			reason_id: data.reason[0],
			reason_title: data.reason_title,
			item_urls: [data.youtubeURL],
			block_regions: data.regions
		})
	}
}

export class BlockList {
	static toJSON(data) {
		const isUnblock = _.isNull(data.reason) || _.isEmpty(data.regions);
		return isUnblock ? _.extend(new this(), {
			item_urls: data.urlsList
		}) :  _.extend(new this(), {
			reason_id: data.reason[0],
			reason_title: data.reason_title,
			item_urls: data.urlsList,
			block_regions: data.regions
		})
	}
}

export class CPRScriptItem {
	static fromJSON(json) {
		return _.extend(new this(), {
			message: json.message
		})
	}
	static toJSON(data) {
		return _.extend(new this(), {
			method: data.method
		})
	}
}
